import qs from "qs";
import { useEffectOnce } from "usehooks-ts";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import { SearchActions } from "src/search/types";
import { serializeContentView, unserializeContentView } from "./useContentView";
import { serializeDocument, unserializeDocument } from "./useExploreDocument";
import { ExploreContext } from "../useExploreContext";
import { serializeTabs, unserializeTabs } from "./useTabs";

export function useQueryStringSync(context: ExploreContext) {
  const router = useRouter();
  const routerRef = useRef(router);
  routerRef.current = router;
  useEffectOnce(() => pushOrReplaceTimer.start());

  const exploreRef = useRef(context);
  exploreRef.current = context;

  // Track our live serialized states
  const liveDoc = serializeDocument(context.doc);
  const liveView = serializeContentView(context.view);
  const liveTabs = serializeTabs(context.tabs);
  const liveState = context.params.serialized;
  // const liveEntity = context.entity.uuid;
  const liveQs = qs.stringify({
    d: liveDoc,
    // e: liveEntity,
    s: liveState,
    t: liveTabs,
    v: liveView,
  });

  // Track our qs serialized states
  const queryString = useSearchParams();
  const qsDoc = queryString.get("d");
  const qsView = queryString.get("v");
  const qsTabs = queryString.get("t");
  const qsState = queryString.get("s");
  // const qsEntity = queryString.get("e");
  const currentQs = qs.stringify({
    d: qsDoc,
    // e: qsEntity,
    s: qsState,
    t: qsTabs,
    v: qsView,
  });

  // Treat the first pass as pushed already..
  const pushedRef = useRef(currentQs);

  // Watch for changes to the liveQs. When it changes, make sure it
  // is different than the existing value, and proceed.
  useEffect(() => {
    if (liveQs && liveQs !== pushedRef.current) {
      pushedRef.current = liveQs;
      const method = pushOrReplaceTimer.method();
      routerRef.current[method](`${window.location.pathname}?${liveQs}`);
    }
  }, [liveQs]);

  // Now we watch for changes in the query string. If that change
  // matches the pushed state, it's already been handled. Otherwise,
  // we need to call methods on the explore context to restore the state.
  useEffect(() => {
    if (currentQs && currentQs !== pushedRef.current) {
      pushedRef.current = currentQs;
      if (qsDoc) {
        const doc = unserializeDocument(qsDoc);
        exploreRef.current.doc.setTitle(doc.title);
        exploreRef.current.doc.setSaved(doc.saved);
      }
      if (qsView) {
        const view = unserializeContentView(qsView);
        exploreRef.current.view.set(view.value);
        exploreRef.current.view.setConfig(view.config);
      }
      if (qsTabs) {
        const tabs = unserializeTabs(qsTabs);
        exploreRef.current.tabs.setFullscreen(tabs.fullscreen);
      }
      if (qsState)
        exploreRef.current.params.dispatch({
          type: SearchActions.loadState,
          value: qsState,
        });
      else {
        // they have renavigated to the app page via the main menu, probably.
      }
      // if (qsEntity) exploreRef.current.entity.open({ uuid: qsEntity });
      // else if (exploreRef.current.entity.uuid)
      //   exploreRef.current.entity.close();
    }
  }, [currentQs, qsDoc, qsView, qsState, /*qsEntity, */ qsTabs]);
}

export const pushOrReplaceTimer = (() => {
  let init: number = Date.now();
  let last: number = Date.now();
  return {
    start: () => {
      init = Date.now();
    },
    ping: () => {
      last = Date.now();
    },
    method: () => {
      const now = Date.now();
      const stabilizing = now - init < 1000;
      const smashing = now - last < 300;
      last = now;
      if (stabilizing || smashing) return "replace";
      return "push";
    },
  };
})();
